/**
 * Goals Service
 * Handles CRUD operations, progress tracking, and outcome evaluation for user goals.
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  limit,
  Unsubscribe,
  Timestamp,
  deleteField,
} from 'firebase/firestore';
import { db } from '../firebase';
import type {
  UserGoal,
  GoalType,
  GoalStatus,
  GoalConfig,
  GoalsSummary,
  GoalOutcome,
  GoalProgressSnapshot,
  CategorySpendGoalConfig,
  TotalSpendGoalConfig,
  TotalDebtGoalConfig,
  SpecificDebtPayoffGoalConfig,
  NoNewDebtGoalConfig,
  RegretRatioGoalConfig,
  AppSessionsGoalConfig,
  SavingsGoalConfig,
} from '../../types';
import { getPfmCategoryDisplayName } from '../../constants/pfmCategories';

/** Ensure a category name is display-friendly, not a raw PFM key */
const ensureCategoryDisplayName = (name: string): string => {
  if (/^[A-Z][A-Z0-9_]+$/.test(name)) {
    return getPfmCategoryDisplayName(name);
  }
  return name;
};

// Collection paths - using inline strings to avoid bundler conflicts with userContentService
const USER_CONTENT_COLLECTION = 'userContent';
const GOALS_SUBCOLLECTION = 'goals';

/**
 * Get the goals collection reference for a user
 */
const getGoalsCollectionRef = (uid: string) => {
  return collection(db, USER_CONTENT_COLLECTION, uid, GOALS_SUBCOLLECTION);
};

/**
 * Get a specific goal document reference
 */
const getGoalDocRef = (uid: string, goalId: string) => {
  return doc(db, USER_CONTENT_COLLECTION, uid, GOALS_SUBCOLLECTION, goalId);
};

/**
 * Generate a unique goal ID
 */
const generateGoalId = (): string => {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 8);
  return `goal_${timestamp}_${random}`;
};

/**
 * Get current UTC date string (local to this module)
 */
const goalUtcDateString = (date: Date = new Date()): string => {
  return date.toISOString().slice(0, 10);
};

/**
 * Get UTC ISO timestamp (local to this module)
 */
const goalUtcIsoString = (date: Date = new Date()): string => {
  return date.toISOString();
};

/**
 * Calculate target value based on goal config and baseline
 */
export const calculateTargetValue = (config: GoalConfig, baselineValue: number): number => {
  switch (config.type) {
    case 'category_spend':
      // Use direct targetAmount if set, otherwise calculate from percentage
      if (config.targetAmount !== undefined) {
        return config.targetAmount;
      }
      return baselineValue * (1 - config.reductionPercent / 100);
    case 'total_spend':
      // Use direct targetAmount if set, otherwise calculate from percentage
      if (config.targetAmount !== undefined) {
        return config.targetAmount;
      }
      return baselineValue * (1 - config.reductionPercent / 100);
    case 'total_debt':
      return Math.max(0, baselineValue - config.reductionAmount);
    case 'specific_debt_payoff':
      return config.targetBalance;
    case 'no_new_debt':
      return 0; // Target is zero new accounts
    case 'regret_ratio':
      return config.targetPercent;
    case 'app_sessions':
      return config.targetSessions;
    case 'emergency_fund':
    case 'life_event':
    case 'monthly_savings':
      return config.targetBalance;
    default:
      return baselineValue;
  }
};

/**
 * Calculate progress percentage (0-100+)
 *
 * IMPORTANT: Different goal types have different semantics:
 *
 * - Spend goals (category_spend, total_spend):
 *   currentValue = amount spent since goal startDate
 *   Returns "budget used %" (0 = nothing spent, 100 = budget exhausted)
 *   Can exceed 100% if overspent
 *
 * - Debt goals:
 *   currentValue = current debt balance
 *   Returns "paydown progress %" (0 = no progress, 100 = target reached)
 *
 * - Regret ratio:
 *   currentValue = current regret % in goal period
 *   Returns "improvement progress %" toward target ratio
 *
 * - App sessions:
 *   currentValue = sessions completed since startDate
 *   Returns "completion %" (0 = none, 100 = target reached)
 */
export const calculateProgress = (config: GoalConfig, baselineValue: number, currentValue: number, targetValue: number): number => {
  switch (config.type) {
    case 'category_spend':
    case 'total_spend': {
      // For spend goals: currentValue = amount spent in goal period
      // Progress = budget used percentage (can exceed 100%)
      if (targetValue <= 0) return currentValue > 0 ? 100 : 0;
      const budgetUsed = (currentValue / targetValue) * 100;
      return Math.max(0, budgetUsed); // Don't cap at 100 - allow overspend to show
    }
    case 'total_debt':
    case 'specific_debt_payoff': {
      // For debt goals: progress = how much of the reduction has been achieved
      // currentValue = current balance (total or single account)
      const totalReduction = baselineValue - targetValue;
      if (totalReduction <= 0) return 100;
      const currentReduction = baselineValue - currentValue;
      const progress = (currentReduction / totalReduction) * 100;
      return Math.max(0, Math.min(100, progress));
    }
    case 'no_new_debt': {
      // no_new_debt uses time-based progress when clean — handled by calculator override
      // Here we return 0 if new debt detected, 100 if clean
      // currentValue = count of new accounts (0 = clean)
      return currentValue === 0 ? 100 : 0;
    }
    case 'regret_ratio': {
      // For regret ratio: progress = how close to target ratio
      // If baseline was 30% regret and target is 10%, and current is 20%
      // Progress = (30 - 20) / (30 - 10) = 50%
      if (baselineValue <= targetValue) return 100;
      const improvement = baselineValue - currentValue;
      const targetImprovement = baselineValue - targetValue;
      const progress = (improvement / targetImprovement) * 100;
      return Math.max(0, Math.min(100, progress));
    }
    case 'app_sessions': {
      // For sessions: simple percentage of target completed
      if (targetValue <= 0) return 100;
      const progress = (currentValue / targetValue) * 100;
      return Math.max(0, progress); // Don't cap - allow exceeding target
    }
    case 'emergency_fund':
    case 'life_event':
    case 'monthly_savings': {
      // For savings goals: progress = balance growth toward target
      // currentValue = current account balance
      const totalGrowthNeeded = targetValue - baselineValue;
      if (totalGrowthNeeded <= 0) return 100;
      const currentGrowth = currentValue - baselineValue;
      const progress = (currentGrowth / totalGrowthNeeded) * 100;
      return Math.max(0, Math.min(100, progress));
    }
    default:
      return 0;
  }
};

/**
 * Generate a human-readable title for a goal
 */
export const generateGoalTitle = (config: GoalConfig): string => {
  switch (config.type) {
    case 'category_spend': {
      const catName = ensureCategoryDisplayName(config.categoryName);
      if (config.targetAmount !== undefined) {
        return `Limit ${catName} to $${config.targetAmount.toLocaleString()}`;
      }
      return `Cut ${catName} by ${config.reductionPercent}%`;
    }
    case 'total_spend':
      if (config.targetAmount !== undefined) {
        return `Limit Spending to $${config.targetAmount.toLocaleString()}`;
      }
      return `Reduce Total Spending by ${config.reductionPercent}%`;
    case 'total_debt':
      return `Pay Down $${config.reductionAmount.toLocaleString()} in Debt`;
    case 'specific_debt_payoff':
      return config.targetBalance === 0
        ? `Pay Off ${config.accountName}`
        : `Pay Down ${config.accountName}`;
    case 'no_new_debt':
      return 'No New Debt';
    case 'regret_ratio':
      return `Keep Regrets Under ${config.targetPercent}%`;
    case 'app_sessions':
      return `Complete ${config.targetSessions} Sessions`;
    case 'emergency_fund':
      return `Save $${config.targetBalance.toLocaleString()} for Emergency Fund`;
    case 'life_event':
      return `Save $${config.targetBalance.toLocaleString()} for ${config.eventName || 'Life Event'}`;
    case 'monthly_savings':
      return `Grow Savings to $${config.targetBalance.toLocaleString()}`;
    default:
      return 'Financial Goal';
  }
};

/**
 * Generate a description for a goal
 */
export const generateGoalDescription = (config: GoalConfig, baselineValue: number, targetDate: string): string => {
  const targetDateFormatted = new Date(targetDate + 'T00:00:00Z').toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC'
  });

  switch (config.type) {
    case 'category_spend': {
      const catName = ensureCategoryDisplayName(config.categoryName).toLowerCase();
      if (config.targetAmount !== undefined) {
        if (baselineValue > 0) {
          return `Keep your ${catName} spending under $${config.targetAmount.toLocaleString()} per month by ${targetDateFormatted}. You're currently at $${baselineValue.toFixed(0)}.`;
        }
        return `Keep your ${catName} spending under $${config.targetAmount.toLocaleString()} per month by ${targetDateFormatted}.`;
      }
      const targetSpend = baselineValue * (1 - config.reductionPercent / 100);
      return `Reduce your ${catName} spending from $${baselineValue.toFixed(0)} to $${targetSpend.toFixed(0)} per month by ${targetDateFormatted}.`;
    }
    case 'total_spend':
      if (config.targetAmount !== undefined) {
        if (baselineValue > 0) {
          return `Keep your total spending under $${config.targetAmount.toLocaleString()} per month by ${targetDateFormatted}. You're currently at $${baselineValue.toFixed(0)}.`;
        }
        return `Keep your total spending under $${config.targetAmount.toLocaleString()} per month by ${targetDateFormatted}.`;
      }
      const targetTotal = baselineValue * (1 - config.reductionPercent / 100);
      return `Reduce your total monthly spending from $${baselineValue.toFixed(0)} to $${targetTotal.toFixed(0)} by ${targetDateFormatted}.`;
    case 'total_debt':
      return `Pay down $${config.reductionAmount.toLocaleString()} of your $${baselineValue.toLocaleString()} total debt by ${targetDateFormatted}.`;
    case 'specific_debt_payoff': {
      if (config.targetBalance === 0) {
        return `Pay off your ${config.accountName} (${config.accountInstitution}) completely by ${targetDateFormatted}. Current balance: $${baselineValue.toLocaleString()}.`;
      }
      const reduction = baselineValue - config.targetBalance;
      return `Reduce your ${config.accountName} (${config.accountInstitution}) balance by $${reduction.toLocaleString()} by ${targetDateFormatted}. Current balance: $${baselineValue.toLocaleString()}.`;
    }
    case 'no_new_debt':
      return `Avoid taking on any new debt by ${targetDateFormatted}. You currently have ${config.baselineAccountCount} debt account${config.baselineAccountCount !== 1 ? 's' : ''}. Connecting existing debts you already have won't count against you.`;
    case 'regret_ratio':
      return `Keep your regret spending under ${config.targetPercent}% of total spending by ${targetDateFormatted}. You're currently at ${baselineValue.toFixed(0)}%.`;
    case 'app_sessions':
      return `Complete ${config.targetSessions} wellness sessions by ${targetDateFormatted}.`;
    case 'emergency_fund':
      return `Grow your emergency fund (${config.accountName} at ${config.accountInstitution}) to $${config.targetBalance.toLocaleString()} by ${targetDateFormatted}. Current balance: $${baselineValue.toLocaleString()}.`;
    case 'life_event':
      return `Save $${config.targetBalance.toLocaleString()} for ${config.eventName || 'your life event'} in ${config.accountName} (${config.accountInstitution}) by ${targetDateFormatted}. Current balance: $${baselineValue.toLocaleString()}.`;
    case 'monthly_savings':
      return `Grow your savings in ${config.accountName} (${config.accountInstitution}) to $${config.targetBalance.toLocaleString()} by ${targetDateFormatted}. Current balance: $${baselineValue.toLocaleString()}.`;
    default:
      return 'Work towards your financial goal.';
  }
};

/**
 * Create a new goal
 */
export const createGoal = async (
  uid: string,
  config: GoalConfig,
  baselineValue: number,
  targetDate: string,
  baselinePeriodDays: number = 30
): Promise<UserGoal> => {
  const goalId = generateGoalId();
  const now = goalUtcIsoString();
  const today = goalUtcDateString();
  
  const targetValue = calculateTargetValue(config, baselineValue);
  const title = generateGoalTitle(config);
  const description = generateGoalDescription(config, baselineValue, targetDate);

  const goal: UserGoal = {
    id: goalId,
    uid,
    type: config.type,
    title,
    description,
    config,
    startDate: today,
    targetDate,
    createdAt: now,
    updatedAt: now,
    baselineValue,
    baselineDate: today,
    baselinePeriodDays,
    targetValue,
    status: 'active',
    currentValue: baselineValue,
    progressPercent: 0,
    lastUpdatedValue: now,
    outcomeProcessed: false,
    progressHistory: [{
      date: today,
      currentValue: baselineValue,
      progressPercent: 0,
      onTrack: true
    }]
  };

  // The first goal a person ever creates is starred for Home by default
  // (2026-10-06). After that the star is theirs: it is never moved by code,
  // only from the Goals page — so the check is "no goal exists yet", never
  // "nothing is starred".
  const goalsRef = getGoalsCollectionRef(uid);
  const existing = await getDocs(query(goalsRef, limit(1)));
  const firstEver = existing.empty;

  const goalRef = getGoalDocRef(uid, goalId);
  await setDoc(goalRef, goal);
  if (firstEver) {
    await setFeaturedGoal(uid, goalId).catch(() => { /* the goal exists; Home falls back to the newest active goal */ });
  }

  return goal;
};

/**
 * Get all goals for a user
 */
export const getUserGoals = async (uid: string): Promise<UserGoal[]> => {
  const goalsRef = getGoalsCollectionRef(uid);
  const q = query(goalsRef, orderBy('createdAt', 'desc'));
  const snapshot = await getDocs(q);
  
  return snapshot.docs.map(doc => ({
    ...doc.data(),
    id: doc.id
  })) as UserGoal[];
};

/**
 * Get active goals for a user
 */
export const getActiveGoals = async (uid: string): Promise<UserGoal[]> => {
  const goalsRef = getGoalsCollectionRef(uid);
  const q = query(
    goalsRef,
    where('status', '==', 'active'),
    orderBy('targetDate', 'asc')
  );
  const snapshot = await getDocs(q);
  
  return snapshot.docs.map(doc => ({
    ...doc.data(),
    id: doc.id
  })) as UserGoal[];
};

/**
 * Get a single goal by ID
 */
export const getGoal = async (uid: string, goalId: string): Promise<UserGoal | null> => {
  const goalRef = getGoalDocRef(uid, goalId);
  const snapshot = await getDoc(goalRef);
  
  if (!snapshot.exists()) return null;
  
  return {
    ...snapshot.data(),
    id: snapshot.id
  } as UserGoal;
};

/**
 * Update goal progress
 */
export const updateGoalProgress = async (
  uid: string,
  goalId: string,
  currentValue: number
): Promise<UserGoal | null> => {
  const goalRef = getGoalDocRef(uid, goalId);
  const snapshot = await getDoc(goalRef);
  
  if (!snapshot.exists()) return null;
  
  const goal = snapshot.data() as UserGoal;
  const now = goalUtcIsoString();
  const today = goalUtcDateString();
  
  const progressPercent = calculateProgress(
    goal.config,
    goal.baselineValue,
    currentValue,
    goal.targetValue
  );
  
  // Determine if on track (linear interpolation)
  const startMs = new Date(goal.startDate + 'T00:00:00Z').getTime();
  const targetMs = new Date(goal.targetDate + 'T00:00:00Z').getTime();
  const nowMs = Date.now();
  const timeProgress = Math.min(100, ((nowMs - startMs) / (targetMs - startMs)) * 100);
  const onTrack = progressPercent >= timeProgress * 0.8; // 80% buffer for being "on track"
  
  const progressSnapshot: GoalProgressSnapshot = {
    date: today,
    currentValue,
    progressPercent,
    onTrack
  };
  
  // Add to history (keep last 30 entries)
  const history = goal.progressHistory || [];
  const updatedHistory = [...history, progressSnapshot].slice(-30);
  
  const updates = {
    currentValue,
    progressPercent,
    lastUpdatedValue: now,
    updatedAt: now,
    progressHistory: updatedHistory
  };
  
  await updateDoc(goalRef, updates);
  
  return { ...goal, ...updates };
};

/**
 * Number of days after targetDate to keep recalculating goal progress.
 * 1 day covers Plaid/Spinwheel sync lag — a payment made on the target date may
 * not arrive until the following day. Any data that lands after this window is
 * genuinely post-deadline and shouldn't flip the outcome.
 */
export const OUTCOME_GRACE_PERIOD_DAYS = 1;

/**
 * Returns true if today falls within the 1-day grace window after the goal's targetDate.
 * Goals in the grace period continue to be recalculated and can have their outcome updated
 * to account for transactions that were dated within the goal window but arrived late via sync.
 */
export function isInGracePeriod(goal: UserGoal): boolean {
  const today = goalUtcDateString();
  if (today <= goal.targetDate) return false;
  const targetMs = new Date(goal.targetDate + 'T00:00:00Z').getTime();
  const daysPast = Math.floor((Date.now() - targetMs) / (1000 * 60 * 60 * 24));
  return daysPast <= OUTCOME_GRACE_PERIOD_DAYS;
}

/**
 * Update a goal's outcome during the 1-day grace period.
 * When the outcome flips (missed→completed or completed→missed), all outcome fields are
 * updated consistently: status, outcomeMessage, and outcomeDate.
 * The goal outcome focus item is also updated if the user hasn't seen it yet.
 * Does not touch outcomeProcessed (first-run processing is tracked separately).
 *
 * Pass the goal with the freshly-calculated progressPercent so message generation
 * uses current data rather than the stale Firestore value.
 */
export const finalizeGoalStatus = async (
  uid: string,
  goalId: string,
  status: 'completed' | 'missed',
  goal?: UserGoal
): Promise<void> => {
  const goalRef = getGoalDocRef(uid, goalId);
  const now = goalUtcIsoString();
  const success = status === 'completed';

  const updates: Record<string, unknown> = {
    status,
    updatedAt: now,
  };

  // When the outcome flips, do a complete re-evaluation of all outcome fields so
  // the stored data is internally consistent (no 'completed' goal with encouragement text).
  if (goal && goal.status !== status) {
    const outcomeMessage = success
      ? generateSuccessMessage(goal)
      : generateEncouragementMessage(goal);

    updates.outcomeMessage = outcomeMessage;
    updates.outcomeDate = now;
    updates.celebrationSeen = false; // Reset so the celebration modal shows the updated outcome
  }

  await updateDoc(goalRef, updates);
};

/**
 * Update a goal's config (e.g., when the linked account changes).
 * Preserves all other goal fields.
 */
export const updateGoalConfig = async (
  uid: string,
  goalId: string,
  config: GoalConfig
): Promise<void> => {
  const goalRef = getGoalDocRef(uid, goalId);
  await updateDoc(goalRef, {
    config,
    updatedAt: goalUtcIsoString()
  });
};

/**
 * Cancel a goal
 */
export const cancelGoal = async (uid: string, goalId: string): Promise<void> => {
  const goalRef = getGoalDocRef(uid, goalId);
  await updateDoc(goalRef, {
    status: 'cancelled' as GoalStatus,
    updatedAt: goalUtcIsoString()
  });
};

/**
 * Delete a goal
 */
export const deleteGoal = async (uid: string, goalId: string): Promise<void> => {
  const goalRef = getGoalDocRef(uid, goalId);
  await deleteDoc(goalRef);
};

/**
 * The goal Home leads with — starred on the Goals page (2026-10-06). One per
 * person, on the root doc, so starring one goal un-stars the rest by
 * construction. `null` clears it; Home then falls back to the newest active
 * goal, which is also what happens once the starred goal ends.
 */
export const setFeaturedGoal = async (uid: string, goalId: string | null): Promise<void> => {
  await setDoc(
    doc(db, USER_CONTENT_COLLECTION, uid),
    { featuredGoalId: goalId ?? deleteField() },
    { merge: true },
  );
};

/**
 * Evaluate goal outcome (called when target date is reached)
 */
export const evaluateGoalOutcome = async (uid: string, goalId: string): Promise<GoalOutcome | null> => {
  const goalRef = getGoalDocRef(uid, goalId);
  const snapshot = await getDoc(goalRef);
  
  if (!snapshot.exists()) return null;
  
  const goal = snapshot.data() as UserGoal;
  
  // Already processed
  if (goal.outcomeProcessed) return null;
  
  const now = goalUtcIsoString();
  const success = goal.progressPercent >= 100;
  
  // Calculate improvement
  let improvementPercent = 0;
  if (goal.baselineValue > 0) {
    const change = goal.baselineValue - goal.currentValue;
    improvementPercent = (change / goal.baselineValue) * 100;
  }
  
  // Generate outcome message
  let message: string;
  if (success) {
    message = generateSuccessMessage(goal);
  } else {
    message = generateEncouragementMessage(goal);
  }
  
  const outcome: GoalOutcome = {
    goalId: goal.id,
    goalType: goal.type,
    title: goal.title,
    success,
    baselineValue: goal.baselineValue,
    targetValue: goal.targetValue,
    finalValue: goal.currentValue,
    improvementPercent,
    message,
    celebratory: success
  };
  
  // Update goal status
  await updateDoc(goalRef, {
    status: success ? 'completed' : 'missed',
    outcomeProcessed: true,
    outcomeDate: now,
    outcomeMessage: message,
    updatedAt: now
  });
  
  return outcome;
};

/**
 * Generate a success message
 */
const generateSuccessMessage = (_goal: UserGoal): string => {
  return 'You achieved your goal! Take a moment to celebrate your growth \u2013 then build on this momentum.';
};

/**
 * Generate an encouragement message for missed goals
 */
const generateEncouragementMessage = (goal: UserGoal): string => {
  const progress = goal.progressPercent;

  if (progress >= 60) {
    return `${progress.toFixed(0)}% progress toward ${goal.title}. That\u2019s real change \u2013 keep challenging yourself with new goals.`;
  }
  return `You missed ${goal.title}. Consider what you could have done differently. Then, find a new goal to get excited about!`;
};

/**
 * Mark a goal's celebration as seen
 */
export const markCelebrationSeen = async (uid: string, goalId: string): Promise<void> => {
  const goalRef = getGoalDocRef(uid, goalId);
  await updateDoc(goalRef, {
    celebrationSeen: true,
    updatedAt: goalUtcIsoString()
  });
};

/**
 * Get goals summary for a user
 */
export const getGoalsSummary = async (uid: string): Promise<GoalsSummary> => {
  const goals = await getUserGoals(uid);
  
  const activeGoals = goals.filter(g => g.status === 'active').length;
  const completedGoals = goals.filter(g => g.status === 'completed').length;
  const missedGoals = goals.filter(g => g.status === 'missed').length;
  
  // Calculate streak (consecutive completed goals by targetDate)
  const sortedGoals = goals
    .filter(g => g.status === 'completed' || g.status === 'missed')
    .sort((a, b) => new Date(b.targetDate).getTime() - new Date(a.targetDate).getTime());
  
  let currentStreak = 0;
  for (const goal of sortedGoals) {
    if (goal.status === 'completed') {
      currentStreak++;
    } else {
      break;
    }
  }
  
  return {
    activeGoals,
    completedGoals,
    missedGoals,
    totalGoals: goals.length,
    currentStreak
  };
};

/**
 * Subscribe to goals for real-time updates
 */
export const subscribeToGoals = (
  uid: string,
  callback: (goals: UserGoal[]) => void
): Unsubscribe => {
  const goalsRef = getGoalsCollectionRef(uid);
  const q = query(goalsRef, orderBy('createdAt', 'desc'));
  
  return onSnapshot(q, (snapshot) => {
    const goals = snapshot.docs.map(doc => ({
      ...doc.data(),
      id: doc.id
    })) as UserGoal[];
    callback(goals);
  }, (error) => {
    console.error('Error subscribing to goals:', error);
    callback([]);
  });
};

/**
 * Subscribe to active goals only
 */
export const subscribeToActiveGoals = (
  uid: string,
  callback: (goals: UserGoal[]) => void
): Unsubscribe => {
  const goalsRef = getGoalsCollectionRef(uid);
  const q = query(
    goalsRef,
    where('status', '==', 'active'),
    orderBy('targetDate', 'asc')
  );
  
  return onSnapshot(q, (snapshot) => {
    const goals = snapshot.docs.map(doc => ({
      ...doc.data(),
      id: doc.id
    })) as UserGoal[];
    callback(goals);
  }, (error) => {
    console.error('Error subscribing to active goals:', error);
    callback([]);
  });
};

/**
 * Check for goals that need outcome evaluation (target date has passed)
 */
export const checkPendingOutcomes = async (uid: string): Promise<GoalOutcome[]> => {
  const today = goalUtcDateString();
  const goals = await getActiveGoals(uid);
  const outcomes: GoalOutcome[] = [];
  
  for (const goal of goals) {
    if (goal.targetDate <= today && !goal.outcomeProcessed) {
      const outcome = await evaluateGoalOutcome(uid, goal.id);
      if (outcome) {
        outcomes.push(outcome);
      }
    }
  }
  
  return outcomes;
};

/**
 * Get the display name for a goal type
 */
export const getGoalTypeDisplayName = (type: GoalType): string => {
  switch (type) {
    case 'category_spend':
      return 'Category Spending';
    case 'total_spend':
      return 'Total Spending';
    case 'total_debt':
      return 'Debt Paydown';
    case 'specific_debt_payoff':
      return 'Specific Debt Payoff';
    case 'no_new_debt':
      return 'No New Debt';
    case 'regret_ratio':
      return 'Purchase Mindfulness';
    case 'app_sessions':
      return 'Wellness Practice';
    case 'emergency_fund':
      return 'Emergency Fund';
    case 'life_event':
      return 'Life Event Savings';
    case 'monthly_savings':
      return 'Monthly Savings';
    default:
      return 'Financial Goal';
  }
};

/**
 * Get goal type icon name (for use with Icons component)
 */
export const getGoalTypeIcon = (type: GoalType): string => {
  switch (type) {
    case 'category_spend':
      return 'ShoppingBag';
    case 'total_spend':
      return 'DollarSign';
    case 'total_debt':
      return 'TrendingDown';
    case 'specific_debt_payoff':
      return 'Target';
    case 'no_new_debt':
      return 'CreditCard';
    case 'regret_ratio':
      return 'Heart';
    case 'app_sessions':
      return 'Target';
    case 'emergency_fund':
      return 'Shield';
    case 'life_event':
      return 'Star';
    case 'monthly_savings':
      return 'TrendingUp';
    default:
      return 'Star';
  }
};

/**
 * Create a focus item for a goal outcome (celebration or feedback)
 * This adds a special card to the user's focus items for the day.
 */
export const createGoalOutcomeFocusItem = async (
  uid: string,
  outcome: GoalOutcome
): Promise<void> => {
  const focusRef = collection(db, 'userContent', uid, 'focus');
  const now = goalUtcIsoString();
  const today = goalUtcDateString();
  
  const focusItem = {
    id: `goal_outcome_${outcome.goalId}`,
    libraryId: `goal_outcome_${outcome.goalId}`,
    topic: outcome.success ? 'celebration' : 'encouragement',
    title: outcome.success ? 'Goal Achieved!' : 'Goal Journey Complete',
    subheader: outcome.title,
    dialogue: outcome.message,
    completionMessage: outcome.success
      ? 'Keep building on this momentum!'
      : 'Every step forward counts. What\'s next?',
    cardType: 'check-in',
    category: 'mindset',
    status: 'active',
    activeDate: today,
    basePriority: 100, // High priority to show at top
    rankScore: 100,
    isLayupItem: false,
    isGoalOutcome: true,
    points: outcome.success ? 5 : 2,
    createdAt: now,
    updatedAt: now,
    // Goal-specific metadata
    goalOutcome: {
      goalId: outcome.goalId,
      goalType: outcome.goalType,
      success: outcome.success,
      improvementPercent: outcome.improvementPercent
    }
  };

  const focusDocRef = doc(focusRef, `goal_outcome_${outcome.goalId}`);
  await setDoc(focusDocRef, focusItem);
};

/**
 * Process all pending goal outcomes for a user.
 * Called on day change via useGoalCelebrations — the celebration modal
 * now handles user-facing acknowledgement (replacing the old focus item approach).
 */
export const processGoalOutcomes = async (uid: string): Promise<GoalOutcome[]> => {
  return await checkPendingOutcomes(uid);
};
